import React, { useEffect, useCallback, useState, useMemo } from "react";
import axios from "axios";
import { useIntl } from "react-intl";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { X } from "lucide-react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { formatRegionMessage, humanizeRegion } from "bundles/common/utils/utils";
import DataTable from "bundles/Internal/components/common/DataTable";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import ExternalProfilesTableFilters, {
  EMPTY_EP_FILTERS,
} from "bundles/Internal/components/external_profile/ExternalProfilesTableFilters";
import PipelinePickerDialog from "bundles/Internal/components/common/PipelinePickerDialog";
import CreateProfileDrawer from "bundles/Internal/components/common/CreateProfileDrawer";
import ProfileDetailPanel from "bundles/Internal/components/common/ProfileDetailPanel";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";
import {
  getOutcomeBadge,
} from "bundles/Internal/components/common/ProfileDetailPanel";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";
import sh from "bundles/common/utils/sh";
import exportCsv from "bundles/common/utils/exportCsv";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";

const EXTERNAL_PROFILE_CREATED_EVENT = "external-profile:created";

function formatFollowUp(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

const callLogSubHeadSx = {
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#2A334E",
  borderBottom: "1px solid rgba(42,51,78,0.1)",
  py: 1,
};

const callLogSubCellSx = {
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "#2A334E",
  py: 0.75,
  borderBottom: "1px solid rgba(42,51,78,0.06)",
  verticalAlign: "top",
};

// Expanded-row content for an external profile: its call-log activity, each
// entry showing WHO made the call (the internal user) and the outcome.
function CallLogsCollapse({ callLogs = [] }) {
  if (!callLogs || callLogs.length === 0) {
    return (
      <Box sx={{ px: 4, py: 2, fontSize: 13, color: "rgba(42,51,78,0.5)" }}>
        No call activity for this profile.
      </Box>
    );
  }

  return (
    <Collapse in timeout="auto" unmountOnExit>
      <Box sx={{ px: 2, py: 1.5, backgroundColor: "rgba(249,250,255,0.7)" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Outcome", "When", "By", "Follow-up", "Notes"].map((h) => (
                <TableCell key={h} sx={callLogSubHeadSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {callLogs.map((log) => {
              const badge = getOutcomeBadge(log.outcome);
              return (
                <TableRow key={log.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={callLogSubCellSx}>
                    <Chip
                      label={badge.label}
                      size="small"
                      sx={{
                        backgroundColor: badge.bg,
                        color: badge.text,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        fontSize: 10,
                        borderRadius: "4px",
                        height: 20,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={callLogSubCellSx}>{formatFollowUp(log.created_at) || "—"}</TableCell>
                  <TableCell sx={callLogSubCellSx}>{log.recruiter_name || log.recruiter_email || "—"}</TableCell>
                  <TableCell sx={callLogSubCellSx}>{formatFollowUp(log.follow_up_at) || "—"}</TableCell>
                  <TableCell sx={{ ...callLogSubCellSx, whiteSpace: "normal", maxWidth: 320 }}>
                    {log.notes || "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Collapse>
  );
}

const EXTERNAL_PROFILE_COLUMNS_BASE = [
  { key: "full_name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "desired_title", label: "Desired title" },
  { key: "region", label: "Region" },
  { key: "cv_url", label: "Resume" },
  { key: "last_action", label: "Last action" },
  { key: "follow_up_at", label: "Follow up" },
  { key: "profile_pipelines", label: "Pipelines" },
  { key: "added_by", label: "Added by" },
  { key: "source", label: "Source" },
];

function renderCell(row, key) {
  const value = row?.[key];

  if (key === "full_name") {
    return row?.full_name || `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
  }

  if (key === "email") {
    const email = value || "";
    if (!email) return "";
    return (
      <Tooltip title={email} arrow placement="top" disableHoverListener={email.length < 20}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 200 }}>
          <Box
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email}
          </Box>
          <CopyButton value={email} sx={{ flexShrink: 0 }} />
        </Box>
      </Tooltip>
    );
  }

  if (key === "phone") {
    const phone = value || "";
    if (!phone) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 160 }}>
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {phone}
        </Box>
        <CopyButton value={phone} sx={{ flexShrink: 0 }} />
      </Box>
    );
  }

  if (key === "region") {
    const label = formatRegionMessage(value);
    if (!label) return "";
    return (
      <Tooltip title={label} arrow placement="top" disableHoverListener={label.length < 16}>
        <Box
          component="span"
          sx={{
            display: "block",
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      </Tooltip>
    );
  }

  if (key === "source") {
    if (!value) return "";
    return humanizeRegion(value);
  }

  if (key === "last_action") {
    const badge = getOutcomeBadge(value);
    const actionAt = row?.last_action_at;
    const tooltipText = actionAt ? formatFollowUp(actionAt) : "";
    return (
      <Tooltip title={tooltipText} arrow placement="top" disableHoverListener={!tooltipText}>
        <Chip
          label={badge.label}
          size="small"
          sx={{
            backgroundColor: badge.bg,
            color: badge.text,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontSize: 11,
            fontWeight: 600,
            borderRadius: "8px",
            height: 24,
          }}
        />
      </Tooltip>
    );
  }

  if (key === "follow_up_at") {
    if (!value) return "";
    const isOverdue = row?.follow_up_date_status === "overdue";
    return (
      <Box
        component="span"
        sx={{
          color: isOverdue ? "#D32F2F" : "inherit",
          fontWeight: isOverdue ? 600 : 400,
        }}
      >
        {formatFollowUp(value)}
      </Box>
    );
  }

  if (key === "profile_pipelines") {
    const pipelines = value || [];
    if (pipelines.length === 0) return "";
    return (
      <Box sx={{ display: "flex", gap: 0.5, overflow: "hidden", maxWidth: 200 }}>
        {pipelines.slice(0, 2).map((pp) => (
          <Chip
            key={pp.id}
            label={pp.title}
            size="small"
            component={Link}
            to={`/internal/pipelines/${pp.id}`}
            clickable
            onClick={(e) => {
              e.stopPropagation();
            }}
            sx={{
              fontSize: 11,
              borderRadius: "6px",
              height: 22,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              backgroundColor: alpha("#1976D2", 0.1),
              color: "#1976D2",
              maxWidth: 100,
              textDecoration: "none",
              "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              "&:hover": { backgroundColor: alpha("#1976D2", 0.2) },
            }}
          />
        ))}
        {pipelines.length > 2 && (
          <Chip
            label={`+${pipelines.length - 2}`}
            size="small"
            sx={{
              fontSize: 11,
              borderRadius: "6px",
              height: 22,
              backgroundColor: "rgba(42,51,78,0.08)",
              color: "#2A334E",
            }}
          />
        )}
      </Box>
    );
  }

  if (key === "added_by") {
    if (!value) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UserAvatar user={value} size={22} />
        <Box component="span">{value.full_name || value.email || "Unknown"}</Box>
      </Box>
    );
  }

  if (key === "cv_url") {
    if (!value) return "";
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#079344", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textDecoration: "none" }}
        onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
        onClick={(e) => e.stopPropagation()}
      >
        Resume ↗
      </a>
    );
  }

  if (key === "desired_title") {
    const title = value || "";
    if (!title) return "";
    return (
      <Tooltip title={title} arrow placement="top" disableHoverListener={title.length < 16}>
        <Box
          component="span"
          sx={{
            display: "block",
            maxWidth: 140,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Box>
      </Tooltip>
    );
  }

  if (value === null || value === undefined) return "";
  return String(value);
}

const BulkActions = ({ selectedRows, onClearSelection, onBulkAddToPipeline }) => (
  <Button
    size="small"
    variant="contained"
    disableRipple
    disableElevation
    onClick={() => onBulkAddToPipeline(selectedRows)}
    sx={{
      textTransform: "none",
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 13,
      borderRadius: "0.6rem",
      backgroundColor: "hsl(150 38% 17%)",
      color: "hsl(80 97% 72%)",
      transition: "opacity 0.15s ease",
      "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
    }}
  >
    Add to pipeline
  </Button>
);

const ExternalProfilesIndexPage = () => {
  const intl = useIntl();
  const authenticity_token = useSelector((s) => s.authenticity_token);
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const columns = useMemo(
    () =>
      EXTERNAL_PROFILE_COLUMNS_BASE.map((col) =>
        col.key === "region"
          ? {
            ...col,
            label: intl.formatMessage({ id: "candidate.region", defaultMessage: "Region" }),
          }
          : col,
      ),
    [intl],
  );

  const renderCellStable = useCallback((row, key) => renderCell(row, key), []);

  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_EP_FILTERS);
  const [internalUsers, setInternalUsers] = useState([]);

  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = useCallback((e, profileId) => {
    e.stopPropagation();
    setExpandedRows((prev) => ({ ...prev, [profileId]: !prev[profileId] }));
  }, []);

  const renderExpandedRow = useCallback(
    (row) => {
      if (!expandedRows[row.id]) return null;
      return <CallLogsCollapse callLogs={row.call_logs || []} />;
    },
    [expandedRows],
  );

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  const [pipelinePickerOpen, setPipelinePickerOpen] = useState(false);
  const [pipelinePickerMode, setPipelinePickerMode] = useState(null);
  const [pipelinePickerIds, setPipelinePickerIds] = useState([]);
  const [pipelinePickerLoading, setPipelinePickerLoading] = useState(false);

  const { openCreateCandidate, updateCandidateModal } = useCandidateModal();

  const fetchProfiles = useCallback(() => {
    setLoading(true);
    const params = {
      page,
      limit: rowsPerPage,
    };

    if (searchQuery.trim()) {
      params.query = searchQuery.trim();
    }

    if (filters.source?.length) params.source = filters.source;
    if (filters.region?.length) params.region = filters.region;
    if (filters.last_action?.length) params.last_action = filters.last_action;
    if (filters.added_by?.length) params.added_by = filters.added_by;

    const endpoint = searchQuery.trim()
      ? "/internal/search/external_profiles"
      : "/internal/external_profiles";

    sh.get(endpoint, { params })
      .then((res) => {
        const data = res.data;
        setProfiles(data.data || []);
        setTotalCount(data.pagy?.count || 0);
        if (data.filter_options?.added_by) {
          setInternalUsers(data.filter_options.added_by);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return; // previous request cancelled by new keystroke
        enqueueSnackbar("Failed to load profiles", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, searchQuery, filters, enqueueSnackbar]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    const profileId = searchParams.get("profile_id");
    if (!profileId) return;
    setDetailOpen(true);
    sh.get(`/internal/external_profiles/${profileId}`)
      .then((res) => setSelectedProfile(res.data))
      .catch(() => setDetailOpen(false));
    const next = new URLSearchParams(searchParams);
    next.delete("profile_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const handler = () => fetchProfiles();
    window.addEventListener(EXTERNAL_PROFILE_CREATED_EVENT, handler);
    return () => window.removeEventListener(EXTERNAL_PROFILE_CREATED_EVENT, handler);
  }, [fetchProfiles]);

  const handlePageChange = useCallback((newPage) => setPage(newPage), []);

  const handleRowsPerPageChange = useCallback((newRpp) => {
    setRowsPerPage(newRpp);
    setPage(1);
  }, []);

  const handleQueryChange = useCallback((q) => {
    setSearchQuery(q);
    setPage(1);
  }, []);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleRowClick = useCallback((row) => {
    setSelectedProfile(row);
    setDetailOpen(true);
    sh.get(`/internal/external_profiles/${row.id}`)
      .then((res) => {
        setSelectedProfile(res.data);
      })
      .catch(() => {});
  }, []);

  const handleLogOutcome = useCallback(
    (outcome, time, notes, followUpAt) => {
      if (!selectedProfile) return;
      const callLogPayload = { outcome };

      if (followUpAt) {
        callLogPayload.follow_up_at = followUpAt;
      } else if (time && outcome === "follow_up_today") {
        const [h, m] = time.split(":");
        const date = new Date();
        date.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
        callLogPayload.follow_up_at = date.toISOString();
      }

      if (notes) callLogPayload.notes = notes;

      sh.post(`/internal/external_profiles/${selectedProfile.id}/call_logs`, {
        authenticity_token,
        call_log: callLogPayload,
      })
        .then((res) => {
          const profile = res?.data?.profile;
          if (!profile) return;
          setProfiles((prev) => prev.map((p) => (String(p.id) === String(profile.id) ? { ...p, ...profile } : p)));
          setSelectedProfile((prev) => (prev && String(prev.id) === String(profile.id) ? { ...prev, ...profile } : prev));
        })
        .catch(() => enqueueSnackbar("Could not log outcome.", { variant: "error" }));
    },
    [authenticity_token, selectedProfile, enqueueSnackbar],
  );

  const handleDetailAddToPipeline = useCallback(() => {
    if (!selectedProfile) return;
    setPipelinePickerMode("single");
    setPipelinePickerIds([selectedProfile.id]);
    setPipelinePickerOpen(true);
  }, [selectedProfile]);

  const handleBulkAddToPipeline = useCallback((ids) => {
    setPipelinePickerMode("bulk");
    setPipelinePickerIds(ids);
    setPipelinePickerOpen(true);
  }, []);

  const handlePipelineConfirm = useCallback(
    (pipelines) => {
      setPipelinePickerLoading(true);
      Promise.all(
        pipelines.map((pipeline) =>
          sh.post(
            `/internal/profile_pipelines/${pipeline.id}/external_profiles/bulk_add`,
            {
              authenticity_token,
              external_profile_ids: pipelinePickerIds,
            },
          ).then((res) => ({ pipeline, ...res.data }))
        ),
      )
        .then((results) => {
          const totalAdded = results.reduce((sum, r) => sum + r.added, 0);
          const totalSkipped = results.reduce((sum, r) => sum + r.skipped, 0);
          const names = results.map((r) => r.pipeline.title).join(", ");
          enqueueSnackbar(`Added ${totalAdded} profile(s) to ${names}${totalSkipped > 0 ? ` (${totalSkipped} already in pipeline)` : ""}`, {
            variant: "success",
          });
          setSelectedRows([]);
          setPipelinePickerOpen(false);
          fetchProfiles();
        })
        .catch(() => enqueueSnackbar("Failed to add profiles to pipeline", { variant: "error" }))
        .finally(() => setPipelinePickerLoading(false));
    },
    [authenticity_token, pipelinePickerIds, enqueueSnackbar, fetchProfiles],
  );

  const handleCreated = useCallback(
    (newProfile) => {
      fetchProfiles();
      setSelectedProfile(newProfile);
      setDetailOpen(true);
    },
    [fetchProfiles],
  );

  const handleEdit = useCallback((profile) => {
    setEditingProfile(profile);
    setEditOpen(true);
  }, []);

  const handleUpdated = useCallback(
    (updatedProfile) => {
      setProfiles((prev) => prev.map((p) => (String(p.id) === String(updatedProfile.id) ? { ...p, ...updatedProfile } : p)));
      setSelectedProfile((prev) => (prev && String(prev.id) === String(updatedProfile.id) ? { ...prev, ...updatedProfile } : prev));
    },
    [],
  );

  const handleCreateCandidate = useCallback((values, { reset, resumeFile, autoEmail, close } = {}) => {
    const appendValue = appendFormValue;

    const externalProfileId = selectedProfile?.id;

    const applyProfileLink = (candidate) => {
      if (!externalProfileId || !candidate?.id) return;
      setProfiles((prev) =>
        prev.map((l) =>
          String(l.id) === String(externalProfileId) ? { ...l, candidate_id: candidate.id } : l
        )
      );
      setSelectedProfile((prev) =>
        prev && String(prev.id) === String(externalProfileId)
          ? { ...prev, candidate_id: candidate.id }
          : prev
      );
    };

    const showCreateWarnings = (candidate) => {
      const warnings = candidate?.warnings;
      if (Array.isArray(warnings) && warnings.length > 0) {
        warnings.forEach((message) => enqueueSnackbar(message, { variant: "warning" }));
      }
    };

    const doPost = (payload) =>
      sh.post("/internal/candidates", payload)
        .then((res) => {
          const candidate = res.data;
          applyProfileLink(candidate);
          showCreateWarnings(candidate);
          return candidate;
        })
        .then(() => {
          enqueueSnackbar("Candidate created", { variant: "success" });
          reset?.();
          close?.();
        })
        .catch((err) => {
          const messages = err?.response?.data?.errors;
          if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
            messages.forEach((message) => enqueueSnackbar(message, { variant: "error" }));
          } else {
            enqueueSnackbar("Failed to create candidate.", { variant: "error" });
          }
        });

    if (!resumeFile) {
      return doPost({
        candidate: values,
        authenticity_token,
        auto_email: autoEmail,
        ...(externalProfileId ? { external_profile_id: externalProfileId } : {}),
      });
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);
    Object.entries(values || {}).forEach(([k, v]) => appendValue(formData, `candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);
    if (autoEmail && typeof autoEmail === "object") {
      Object.entries(autoEmail).forEach(([k, v]) => appendValue(formData, `auto_email[${k}]`, v));
    }
    if (externalProfileId) {
      formData.append("external_profile_id", String(externalProfileId));
    }
    return doPost(formData);
  }, [authenticity_token, enqueueSnackbar, selectedProfile]);

  const handleSignUpToCandidate = useCallback((profile) => {
    openCreateCandidate({
      onSubmit: handleCreateCandidate,
      defaultValues: {
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        region: profile.region || "",
        desired_job_title: profile.desired_title || "",
      },
    });

    if (profile.cv_url) {
      fetch(profile.cv_url)
        .then((res) => res.blob())
        .then((blob) => {
          const name = profile.cv_url.split("/").pop()?.split("?")[0] || "cv.pdf";
          updateCandidateModal({ defaultResumeFile: new File([blob], name, { type: blob.type }) });
        });
    }
  }, [openCreateCandidate, updateCandidateModal, handleCreateCandidate]);

  const handleExportCsv = useCallback((ids) => {
    exportCsv("external_profiles", ids, authenticity_token, EXTERNAL_PROFILE_COLUMNS_BASE)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [authenticity_token, enqueueSnackbar]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <DataTable
        title="External Profiles"
        totalLabel={(count) => `${count.toLocaleString()} total profiles`}
        emptyTitle="No external profiles found"
        emptySubtitle="Try adjusting your search or filters"
        columns={columns}
        data={profiles}
        renderCell={renderCellStable}
        expandable
        isRowExpanded={(row) => !!expandedRows[row.id]}
        onToggleExpandRow={(row, e) => toggleExpand(e, row.id)}
        renderExpandedRow={renderExpandedRow}
        onRowClick={handleRowClick}
        onAddClick={() => setCreateOpen(true)}
        addButtonLabel="Add profile"
        onQueryChange={handleQueryChange}
        searchPlaceholder="Search by name, email, phone…"
        searchInfo="Full name, first name, last name, email, phone, desired title"
        filtersComponent={
          <ExternalProfilesTableFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            internalUsers={internalUsers}
          />
        }
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        bulkActionsComponent={BulkActions}
        bulkActionsProps={{ onBulkAddToPipeline: handleBulkAddToPipeline }}
        onExportCsv={handleExportCsv}
      />

      {/* Detail panel */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 520 },
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          },
        }}
      >
        {selectedProfile && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderBottom: "1px solid hsl(140 8% 88%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                }}
              >
                Profile details
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDetailAddToPipeline}
                  sx={{
                    textTransform: "none",
                    height: 36,
                    px: 1.5,
                    borderRadius: "calc(-2px + 0.75rem)",
                    borderColor: "hsl(140 8% 88%)",
                    backgroundColor: "hsl(140 10% 95%)",
                    color: "hsl(150 30% 12%)",
                    fontSize: 13,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    "&:hover": {
                      borderColor: "hsl(140 8% 88%)",
                      backgroundColor: "rgb(177, 252, 29)",
                      color: "hsl(150 30% 12%)",
                    },
                  }}
                >
                  Add to pipeline
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setDetailOpen(false)}
                  sx={{
                    p: 0.5,
                    borderRadius: "6px",
                    color: "hsl(150 10% 46%)",
                    "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
                  }}
                >
                  <X size={16} />
                </IconButton>
              </Box>
            </Box>

            {/* Panel content */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
              <ProfileDetailPanel
                profile={selectedProfile}
                onLogOutcome={handleLogOutcome}
                onSignUpToCandidate={handleSignUpToCandidate}
                onEdit={handleEdit}
              />
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Create / Edit drawer */}
      <CreateProfileDrawer
        open={createOpen || editOpen}
        onClose={() => { setCreateOpen(false); setEditOpen(false); }}
        onCreated={handleCreated}
        editingProfile={editOpen ? editingProfile : undefined}
        onUpdated={handleUpdated}
      />

      {/* Pipeline picker */}
      <PipelinePickerDialog
        open={pipelinePickerOpen}
        onClose={() => setPipelinePickerOpen(false)}
        onConfirm={handlePipelineConfirm}
        loading={pipelinePickerLoading}
        title={pipelinePickerMode === "bulk" ? `Add ${pipelinePickerIds.length} profiles to pipeline` : "Add to pipeline"}
        confirmLabel={pipelinePickerMode === "bulk" ? `Add ${pipelinePickerIds.length} profiles` : "Add to pipeline"}
      />

    </Box>
  );
};

export default ExternalProfilesIndexPage;
